import React from "react";
import { Check, ArrowRight } from "lucide-react";

// Pricing tiers — align with Booking.jsx size dropdown
export const PRICE_TIERS = [
  {
    id: "half",
    size: '1/2"',
    sizes: ['1/2"'],
    price: 135,
    blurb: "Small residential meter",
    popular: false,
  },
  {
    id: "threequarter",
    size: '3/4"',
    sizes: ['3/4"'],
    price: 150,
    blurb: "Most common residential device",
    popular: true,
  },
  {
    id: "one",
    size: '1"',
    sizes: ['1"'],
    price: 160,
    blurb: "Irrigation, small commercial",
    popular: false,
  },
  {
    id: "onefivep",
    size: '1½" & up',
    sizes: ['1.25"', '1.5"', '2"', '2.5"', '3"', '4"', '6"', '8" or larger'],
    price: 190,
    blurb: "Commercial, fire line, large assemblies",
    popular: false,
  },
];

// Leak-repair flat rate — applied when customer answers "Yes" to leaking
// < 1½"  => $150 flat
// ≥ 1½"  => $225 flat
const LARGE_SIZES = ['1.25"', '1.5"', '2"', '2.5"', '3"', '4"', '6"', '8" or larger'];

export function priceForSize(sizeLabel) {
  if (!sizeLabel) return null;
  const tier = PRICE_TIERS.find((t) => t.sizes.includes(sizeLabel));
  return tier ? tier.price : null;
}

export function leakRepairFee(sizeLabel) {
  if (!sizeLabel) return null;
  return LARGE_SIZES.includes(sizeLabel) ? 225 : 150;
}

const includes = [
  "Certified on-site test",
  "City of Laredo digital filing",
  "Same-day PDF report",
  "No re-testing fees on first fail",
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="py-20 lg:py-28 bg-white"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="step-num">03 — PRICING</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold mt-3 tracking-tight">
              Flat, honest pricing by device size.
            </h2>
          </div>
          <p className="md:max-w-sm text-slate-600">
            The <strong>$60 booking deposit</strong> credits toward your final invoice. Taxes applied at
            service.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-slate-200">
          {PRICE_TIERS.map((t, i) => (
            <div
              key={t.id}
              className={`relative p-6 lg:p-8 border-r border-b border-slate-200 bg-white transition-colors ${
                t.popular ? "bg-slate-50" : ""
              }`}
              data-testid={`price-tier-${t.id}`}
            >
              {t.popular && (
                <span className="absolute top-0 right-0 bg-orange-500 text-white font-display text-[10px] tracking-[0.18em] font-bold px-3 py-1.5">
                  MOST COMMON
                </span>
              )}
              <div className="overline">Device Size</div>
              <div className="font-display text-5xl font-extrabold tracking-tight mt-2 text-slate-900">
                {t.size}
              </div>
              <div className="text-sm text-slate-600 mt-2 min-h-[40px]">{t.blurb}</div>

              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-4xl font-bold text-slate-900">${t.price}</span>
                <span className="text-sm text-slate-500">+ tax</span>
              </div>
              <div className="text-[11px] tracking-[0.16em] uppercase text-slate-500 font-display mt-1">
                Per certified test
              </div>

              <ul className="mt-6 space-y-2">
                {includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                    {inc}
                  </li>
                ))}
              </ul>

              <a
                href="#booking"
                className="mt-6 inline-flex items-center gap-1 text-sm font-display font-semibold text-blue-600"
                data-testid={`price-book-${t.id}`}
              >
                Book this size <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>

        <p className="text-xs text-slate-500 mt-6 max-w-3xl">
          Repairs, rebuilds, new installations and irrigation PVB work are quoted separately after an
          on-site inspection. Prices reflect a standard certified test; failed devices may require
          additional parts and labor.
        </p>

        {/* Leak repair surcharge */}
        <div
          id="leak-repair"
          className="mt-10 grid sm:grid-cols-[1.1fr_0.9fr] border border-slate-200 bg-slate-50"
          data-testid="leak-repair-info"
        >
          <div className="p-6 lg:p-8 border-b sm:border-b-0 sm:border-r border-slate-200">
            <div className="step-num">IF IT'S LEAKING</div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold mt-2 tracking-tight">
              Flat-rate leak repair.
            </h3>
            <p className="text-slate-600 text-sm mt-3 max-w-md">
              If you mark the device as currently leaking on the booking form, we come with rebuild
              parts and repair it on-site for a flat fee added to the test price. No hourly
              surprise charges.
            </p>
          </div>
          <div className="grid grid-cols-2">
            <div className="p-6 lg:p-8 border-r border-slate-200">
              <div className="overline">Under 1½"</div>
              <div className="font-display text-4xl font-extrabold mt-2 text-slate-900">$150</div>
              <div className="text-xs tracking-[0.14em] uppercase text-slate-500 font-display mt-1">Flat · + tax</div>
            </div>
            <div className="p-6 lg:p-8">
              <div className="overline">1½" &amp; larger</div>
              <div className="font-display text-4xl font-extrabold mt-2 text-slate-900">$225</div>
              <div className="text-xs tracking-[0.14em] uppercase text-slate-500 font-display mt-1">Flat · + tax</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
